import React, { useEffect, useState, useMemo } from 'react';
import { differenceInCalendarDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { getCreditRequestsDetails } from '@atlassian/cssxp-proxies';
import { UserRedirectionData, UserRedirectionType } from '@atlassian/cssxp-typescommon';
import { Net30Application, Net30ApplicationStatus } from '@atlassian/cssxp-types-billing';
import { Maybe, TermPaymentTypes } from '@css-xp/graphtypes/src/generated-types-new';
import { InfoPanel } from '@css-xp/base';
import { useApplicationContext } from '@css-xp/base/common-context';

interface Net30StatusArgs {
  txaId: string;
  paymentMethod?: Maybe<string>;
}

interface Net30StatusBannerArgs {
  isInProgress: boolean;
  openTicketNumber?: Maybe<string>;
  version?: number;
}

const defaultCreditRequests = {
  OPEN: {
    ticketNumber: '',
    createdAt: 0,
  },
  REJECTED: {
    ticketNumber: '',
    createdAt: 0,
  },
};

export const useNet30ApplicationStatus = ({ txaId, paymentMethod }: Net30StatusArgs) => {
  const [creditRequestDetails, setCreditRequestDetails] = useState(defaultCreditRequests);
  const isNet14PaymentMethod = paymentMethod === TermPaymentTypes.NET14;

  useEffect(() => {
    async function fetchCreditRequestDetails() {
      const creditRequests = await getCreditRequestsDetails(txaId);

      if (!creditRequests?.length) {
        return;
      }

      const approvedStatuses = [
        Net30ApplicationStatus.APPROVED,
        Net30ApplicationStatus.EXCEPTION_APPROVED,
        Net30ApplicationStatus.CLOSED,
      ];
      const isNet30Approved = creditRequests?.some((details: Net30Application) =>
        approvedStatuses.includes(details?.status?.toUpperCase() as Net30ApplicationStatus)
      );

      if (isNet30Approved) {
        // So that the banner is not shown if the status is approved
        return;
      }
      const rejectedStatuses = [Net30ApplicationStatus.REJECTED, Net30ApplicationStatus.EXCEPTION_REJECTED];
      const rejectedRequest = creditRequests?.find((details: Net30Application) =>
        rejectedStatuses.includes(details?.status?.toUpperCase() as Net30ApplicationStatus)
      );
      const openStatuses = [
        Net30ApplicationStatus.OPEN,
        Net30ApplicationStatus.PENDING_AR,
        Net30ApplicationStatus.PENDING_CUSTOMER,
        Net30ApplicationStatus.PENDING_INTERNAL,
        Net30ApplicationStatus.AWAITING_APPROVAL,
        Net30ApplicationStatus.FOR_EXCEPTION_APPROVAL,
      ];
      const openRequest = creditRequests?.find((details: Net30Application) =>
        openStatuses.includes(details?.status?.toUpperCase() as Net30ApplicationStatus)
      );

      setCreditRequestDetails({
        OPEN: openRequest ? openRequest : defaultCreditRequests.OPEN,
        REJECTED: rejectedRequest ? rejectedRequest : defaultCreditRequests.REJECTED,
      });
    }

    if (isNet14PaymentMethod) {
      fetchCreditRequestDetails();
    }
  }, [isNet14PaymentMethod, txaId]);

  const applicationStatus = useMemo(() => {
    const diffInDayForOpenRequest = differenceInCalendarDays(Date.now(), Number(creditRequestDetails.OPEN.createdAt));
    const diffInDayForRejectedRequest = differenceInCalendarDays(
      Date.now(),
      Number(creditRequestDetails.REJECTED.createdAt)
    );
    const openTicketNumber = creditRequestDetails.OPEN.ticketNumber;
    const rejectedTicketNumber = creditRequestDetails.REJECTED.ticketNumber;

    const net30Status = {
      isInProgress: false,
      openTicketNumber: openTicketNumber,
      rejectedTicketNumber: rejectedTicketNumber,
    };

    const isApplicationInProgress =
      (!rejectedTicketNumber && !!openTicketNumber) ||
      (!!rejectedTicketNumber &&
        !!openTicketNumber &&
        diffInDayForOpenRequest <= 90 &&
        diffInDayForRejectedRequest >= 90);

    if (isApplicationInProgress) {
      net30Status.isInProgress = true;
    }

    return net30Status;
  }, [creditRequestDetails]);

  return applicationStatus;
};

export const Net30StatusBanner = ({ isInProgress, openTicketNumber, version }: Net30StatusBannerArgs) => {
  const { redirectionData } = useApplicationContext();
  const finOpsRedirectionData = useMemo(
    () => redirectionData?.find((item: UserRedirectionData) => item.name === UserRedirectionType.FinOpsLink),
    [redirectionData]
  );

  const statusBanner = useMemo(() => {
    const finOpsUrl = finOpsRedirectionData?.url;

    if (isInProgress) {
      if (version === 2) {
        return (
          <InfoPanel
            type="information"
            message={
              <p>
                Billing account credit check in progress for Net-30 request.
                {openTicketNumber && finOpsUrl && (
                  <>
                    {' '}
                    View{' '}
                    <Link target="_blank" to={{ pathname: `${finOpsUrl}/${openTicketNumber}` }}>
                      support ticket
                    </Link>{' '}
                    for more information.
                  </>
                )}
              </p>
            }
          />
        );
      }

      return (
        <InfoPanel
          type="information"
          message={
            <p>
              Net-30 payment terms have been requested and we're running a credit check for this billing account.
              {openTicketNumber && finOpsUrl && (
                <>
                  {' '}
                  Review{' '}
                  <Link target="_blank" to={{ pathname: `${finOpsUrl}/${openTicketNumber}` }}>
                    {openTicketNumber}
                  </Link>{' '}
                  for more information.
                </>
              )}
            </p>
          }
        />
      );
    }

    return null;
  }, [finOpsRedirectionData, isInProgress, openTicketNumber]);

  return statusBanner;
};
